import type { InvoiceData, InvoiceTag } from "@/apis/gql/types";
import { Tags } from "@/lib/miscellaneous/tags";
import { useTenant } from "@/providers/session";
import { useLocation, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import debounce from "debounce";
import { Paperclip, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { DateCell } from "@/components/shared/cells/date-cell";
import { SelectCell } from "@/components/shared/cells/select";
import { SupplierCell } from "@/components/shared/cells/supplier";
import { TagsCell } from "@/components/shared/cells/tags";
import { RawTextCell } from "@/components/shared/cells/text";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCallback } from "react";

const selectColumn: ColumnDef<any> = {
  id: "select",
  size: 45,
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => (
    <div className="flex items-center justify-center w-full">
      <SelectCell {...cell} />
    </div>
  ),
  enableSorting: false,
  enableHiding: false,
};

const supplierNameColumn: ColumnDef<any> = {
  accessorKey: "supplier.supplierName",
  id: "supplierName",
  filterFn: (row, columnId, value) => {
    return row.original?.supplier?.supplierId === value;
  },
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Versender" />
  ),
  cell: (cell) => {
    return (
      <SupplierCell
        name={cell.row.original.supplier?.supplierName ?? "Unbekannt"}
        address={cell.row.original.supplier?.address}
      />
    );
  },
};

const documentTypeSummaryColumn: ColumnDef<any> = {
  accessorKey: "invoice.documentTypeSummary",
  id: "documentTypeSummary",
  enableSorting: false,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Inhalt" />
  ),
  cell: (cell) => {
    return (
      <RawTextCell value={cell.row.original.invoice.documentTypeSummary} />
    );
  },
};

const tagsColumn: ColumnDef<any> = {
  accessorKey: "tags",
  id: "tags",
  size: 125,
  enableSorting: false,
  header: (header) => (
    <DataTableColumnHeader
      column={header.column}
      title="Typ"
      style={{ padding: "5px" }}
    />
  ),
  filterFn: (row, columnId, value) => {
    const duplicate = row.original.invoice.isDuplicate;

    if (value.includes(Tags.NoTags)) {
      if (!duplicate) {
        return row.original.tags?.length === 0;
      }
      return false;
    }

    if (value.includes(Tags.DuplicateInvoice) && duplicate) {
      return true;
    }

    const rowTags: any[] = row.original.tags ?? [];

    const tagNames = rowTags?.map((tag) => tag.tagName);

    if (tagNames.length === 0) return false;

    return value.some((tag: string) => tagNames.includes(tag));
  },
  sortingFn: (a, b) => {
    let tagsA = a.original.tags?.map((tag: any) => tag.tagName).sort() ?? [];
    let tagsB = b.original.tags?.map((tag: any) => tag.tagName).sort() ?? [];

    tagsA = tagsA.sort();
    tagsB = tagsB.sort();

    for (let i = 0; i < Math.max(tagsA.length, tagsB.length); i++) {
      if (tagsA[i] === undefined) return 1;
      if (tagsB[i] === undefined) return -1;
      const comparison = tagsA[i].localeCompare(tagsB[i]);
      if (comparison !== 0) return comparison;
    }

    return 0;
  },
  cell: (cell) => {
    const tags = [...(cell.row?.original?.tags ?? [])];

    if (cell.row.original.invoice.isDuplicate) {
      tags.push({
        tagId: "duplicate_invoice",
        tagName: Tags.DuplicateInvoice,
        details: null,
      });
    }

    return <TagsCell tags={tags} />;
  },
};

const createdAtColumn: ColumnDef<any> = {
  accessorKey: "invoice.createdAt",
  size: 130,
  id: "createdAt",
  header: (header) => (
    <DataTableColumnHeader
      column={header.column}
      title="Erstell.Datum"
      style={{ padding: "5px" }}
    />
  ),
  cell: (cell) => <DateCell value={cell.row.original.invoice.createdAt} />,
};

const actionsColumn: (
  onReview: (invoiceId: string) => void,
  onDelete: (invoiceId: string) => void,
  onIsABooking: (invoiceId: string) => Promise<void>,
) => ColumnDef<any> = (onReview, onDelete) => ({
  id: "actions",
  size: 305,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Aktionen" />
  ),
  // @ts-ignore
  cell: ({ isHovered, table, row }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { companySlug } = useTenant();

    const { t } = useTranslation();

    const showRowActions = isHovered || table.getFocusedRowId() === row.id;

    const handleToInvoice = () => {
      navigate({
        to: "/$company/buchhaltung/rechnung/$id/is-booking",
        params: { company: companySlug, id: row.id },
        search: { returnTo: location.href },
      });
    };

    const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onDelete(row.id);
    };

    const handleReview = () => {
      onReview(row.id);
    };

    const debouncedIsABooking = useCallback(
      debounce(
        async (invoiceId: string) => {
          handleToInvoice();
        },
        300,
        { immediate: true },
      ),
      [],
    );

    const handleIsABooking = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      debouncedIsABooking(row.id);
    };

    return (
      <div
        className={`flex space-x-2 transition-opacity duration-200 flex-wrap ${
          showRowActions ? "opacity-100" : "opacity-0"
        }`}
      >
        <TooltipProvider>
          <div className="flex justify-center gap-1">
            {/* TODO disable this for now */}
            {/* <Tooltip>
              <TooltipTrigger>
                <Button variant="outline" size="icon" onClick={handleToInvoice}>
                  <SquareArrowOutUpRight className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("to_invoice")}</TooltipContent>
            </Tooltip> */}
            <Tooltip>
              <TooltipTrigger>
                <Button variant="outline" size="icon" onClick={handleDelete}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("delete")}</TooltipContent>
            </Tooltip>
            <Button
              variant="outline"
              className="gap-1"
              onClick={handleIsABooking}
            >
              <Plus className="w-4 h-4" />
              {t("create_booking")}
            </Button>
            <Button className="gap-1" onClick={handleReview}>
              <Paperclip className="w-4 h-4" />
              {t("not_a_booking")}
            </Button>
          </div>
        </TooltipProvider>
      </div>
    );
  },
  meta: {
    className: "flex-none",
  },
});

const statusColumn: ColumnDef<any> = {
  id: "status",
  accessorKey: "invoice.status",
  filterFn: (row, columnId, value) => {
    if (value.length === 0) return true;

    const status = row.original?.invoice?.status;

    return value.includes(status);
  },
};

const ocrReviewed: ColumnDef<any> = {
  id: "ocrReviewed",
  accessorKey: "invoice.ocrReviewed",
};

const isBooking: ColumnDef<any> = {
  id: "isBooking",
  accessorKey: "invoice.isBooking",
};

const isDuplicate: ColumnDef<any> = {
  id: "isDuplicate",
  accessorKey: "invoice.isDuplicate",
};

export const columns: (
  onReview: (invoiceId: string) => void,
  onDelete: (invoiceId: string) => void,
  onIsABooking: (invoiceId: string) => Promise<void>,
) => ColumnDef<any>[] = (onReview, onDelete, onIsABooking) => [
  selectColumn,
  supplierNameColumn,
  documentTypeSummaryColumn,
  tagsColumn,
  createdAtColumn,
  ocrReviewed,
  isBooking,
  isDuplicate,
  actionsColumn(onReview, onDelete, onIsABooking),
  statusColumn,
];
